"use client";

import { useEffect, useState } from "react";
import { useMediaQuery } from "./use-media-query";

type Phase = "type" | "hold" | "erase" | "pause";
type TypingState = { index: number; length: number; phase: Phase };

const DELAY_MS: Record<Phase, number> = { type: 40, hold: 1800, erase: 16, pause: 350 };
const REDUCED_MOTION_MS = 4000;

const advance = (state: TypingState, fullLength: number): TypingState => {
  switch (state.phase) {
    case "type": {
      const length = state.length + 1;
      return { ...state, length, phase: length >= fullLength ? "hold" : "type" };
    }
    case "hold":
      return { ...state, phase: "erase" };
    case "erase": {
      const length = state.length - 1;
      return { ...state, length, phase: length <= 0 ? "pause" : "erase" };
    }
    case "pause":
      return { index: state.index + 1, length: 0, phase: "type" };
  }
};

/**
 * Types each example into a placeholder, holds it, erases it and moves on. While `paused` it shows
 * the whole current example; with reduced motion it swaps whole examples instead of typing.
 */
export const useTypingPlaceholder = (examples: readonly string[], paused: boolean): string => {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [state, setState] = useState<TypingState>({ index: 0, length: examples[0]?.length ?? 0, phase: "hold" });
  const [wasPaused, setWasPaused] = useState(paused);

  const full = examples[state.index % examples.length] ?? "";

  if (paused !== wasPaused) {
    setWasPaused(paused);
    if (paused) setState({ ...state, length: full.length, phase: "hold" });
  }

  useEffect(() => {
    if (paused || examples.length === 0) return;

    const timer = reduceMotion
      ? setTimeout(() => {
          setState((current) => {
            const index = current.index + 1;
            return { index, length: examples[index % examples.length].length, phase: "hold" };
          });
        }, REDUCED_MOTION_MS)
      : setTimeout(() => setState((current) => advance(current, full.length)), DELAY_MS[state.phase]);

    return () => clearTimeout(timer);
  }, [state, paused, reduceMotion, full.length, examples]);

  return paused || reduceMotion ? full : full.slice(0, state.length);
};
